import type * as Ably from 'ably'

import type { TranscriptConnectionErrorType } from 'src/models/TranscriptConnectionState'
import type { TranscriptCrdtTransportStatus } from 'src/network/TranscriptCrdtTransport'

// Ably uses 5-digit error codes. The first 3 digits mirror the HTTP status
// (e.g. 401xx unauthorized, 403xx forbidden, 5xxxx server errors).
// See https://github.com/ably/ably-common/blob/main/protocol/errors.json
export const ABLY_AUTH_ERROR_RANGE_START = 40100
export const ABLY_AUTH_ERROR_RANGE_END = 40200
export const ABLY_ACCESS_DENIED_RANGE_START = 40300
export const ABLY_ACCESS_DENIED_RANGE_END = 40400

// Synthesized for ErrorInfo built from non-Ably HTTP responses (token endpoint).
// Codes must fall in the canonical ranges so getErrorTypeFromAblyError classifies
// any SDK re-emit identically.
export const ABLY_SYNTHETIC_TOKEN_ERROR_CODE = 40140
export const ABLY_SYNTHETIC_ACCESS_DENIED_CODE = 40300
export const ABLY_SYNTHETIC_SERVER_ERROR_CODE = 50000

// Error types Ably has a canonical code range for. Anything absent here (e.g.
// `not_found`, which has no Ably equivalent) synthesizes the server-error code.
export const ABLY_SYNTHETIC_CODE_BY_ERROR_TYPE: Partial<Record<TranscriptConnectionErrorType, number>> = {
    auth_expired: ABLY_SYNTHETIC_TOKEN_ERROR_CODE,
    access_denied: ABLY_SYNTHETIC_ACCESS_DENIED_CODE,
}

export const ABLY_STATE_TO_TRANSPORT_STATUS: Partial<Record<Ably.ConnectionState, TranscriptCrdtTransportStatus>> = {
    connecting: 'connecting',
    connected: 'connected',
    disconnected: 'disconnected',
}

export const SYNC_RETRY_BASE_DELAY_MS = 2000
export const SYNC_RETRY_MAX_DELAY_MS = 30000

// Ably's `channel.history()` page size. 100 is also the SDK default. History
// replay walks pages newest-first until it crosses the snapshot's
// `lastTimestamp` or runs out of pages; total work is bounded by the gap
// between the snapshot moment and the attach point (capped by Ably's
// retention window), not by a fixed page cap.
export const HISTORY_PAGE_SIZE = 100

// History fetch retry budget before the cycle is treated as fatal. Unlike
// `crdtSync`, history failures are bounded: without it the doc is silently
// missing the post-snapshot tail (up to ~1 min), so escalating after a few
// retries surfaces the problem rather than letting the user view stale data.
export const HISTORY_RETRY_MAX_ATTEMPTS = 3

// Dedicated Ably endpoint for EU deployments. US (and other) regions use the SDK default.
export const ABLY_EU_ENDPOINT = 'verbit-eu'
